import { CardErrorReason } from './../../model/checkout'
import { ErrorCode, isPortalApiError } from '@afterpaytouch/portal-api'
import { handleTopazResultStatus } from './helpers'
import { closeCheckout, confirmNew, finaliseThreeDS, initiateNew, initiateThreeDS, retrieveCards, setSessionExpired, setupPurchase } from '../'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  PaymentMethodType,
  ResultCode,
  SelectedPaymentMethod,
  ThreeDSChallengeStatus,
  ThreeDSecureStatus,
  TopazApiErrorMessage,
  TopazPaymentState,
  TopazStatus,
} from '../../model'
import { PURGE } from 'redux-persist'
import { setupOrder, setupPayment } from './hooks'
import { CashAppPayDetail } from '@afterpay/types'

export interface LastSuccessfulSetup {
  paymentMethod?: SelectedPaymentMethod
  traceId: string
}

export interface TopazState {
  purchase: TopazPaymentState
  lastSuccessfulSetup: LastSuccessfulSetup | undefined
  payment?: string
  order?: string
  hasUserInitiatedChangedPaymentMethod: boolean
  preferredCardError?: CardErrorReason
}

export const topazInitialState: TopazState = {
  purchase: {
    status: TopazStatus.Initial,
  },
  lastSuccessfulSetup: undefined,
  hasUserInitiatedChangedPaymentMethod: false,
}

const topazSlice = createSlice({
  name: 'topaz',
  initialState: topazInitialState,
  reducers: {
    selectPaymentMethod(state: TopazState, action: PayloadAction<SelectedPaymentMethod | undefined>): TopazState {
      return {
        ...state,
        purchase: {
          ...state.purchase,
          status: TopazStatus.Saved,
          selected: action.payload,
        },
      }
    },
    selectPaymentMethodWithoutStatus(state: TopazState, action: PayloadAction<SelectedPaymentMethod | undefined>): TopazState {
      return {
        ...state,
        purchase: {
          ...state.purchase,
          selected: action.payload,
        },
      }
    },
    setHasUserInitiatedChangedPaymentMethod(state: TopazState): TopazState {
      return {
        ...state,
        hasUserInitiatedChangedPaymentMethod: true,
      }
    },
    resetThreeDSState(state: TopazState): TopazState {
      return {
        ...state,
        purchase: { ...state.purchase, threeDS: undefined },
      }
    },
    setLastSuccessfulSetup(state, action: PayloadAction<LastSuccessfulSetup | undefined>): TopazState {
      return {
        ...state,
        lastSuccessfulSetup: action.payload,
      }
    },
    setPreferredCardError(state, action: PayloadAction<CardErrorReason | undefined>): TopazState {
      return {
        ...state,
        preferredCardError: action.payload,
      }
    },
    updateTopazApiErrorMessage(state, action: PayloadAction<TopazApiErrorMessage | undefined>): TopazState {
      return {
        ...state,
        purchase: { ...state.purchase, topazApiErrorMessage: action.payload },
      }
    },
    setCashAppPayDetails(state, action: PayloadAction<CashAppPayDetail>): TopazState {
      return {
        ...state,
        purchase: {
          ...state.purchase,
          selected: {
            type: PaymentMethodType.CASH_APP_PAY,
            cashAppPay: action.payload,
          },
        },
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setupPurchase.pending, (state) => {
        state.purchase.status = TopazStatus.Saving
      })
      .addCase(setupPurchase.rejected, (state) => {
        state.purchase.status = TopazStatus.Error
      })
      .addCase(setupPurchase.fulfilled, (state, action) => {
        const topazResult = handleTopazResultStatus(action.payload)
        state.purchase.status = topazResult
      })
      .addCase(initiateThreeDS.pending, (state) => {
        state.purchase = {
          ...state.purchase,
          threeDS: { status: ThreeDSecureStatus.Initiating },
        }
      })
      .addCase(initiateThreeDS.fulfilled, (state, action) => {
        const { challengeRequired, challengeUri, threeDSId } = action.payload
        state.purchase.threeDS = {
          status: ThreeDSecureStatus.Initiated,
          threeDSId,
          challenge: {
            status: challengeRequired ? ThreeDSChallengeStatus.ChallengeRequired : ThreeDSChallengeStatus.ChallengeNotRequired,
            challengeUri: challengeUri ?? undefined,
          },
        }
      })
      .addCase(initiateThreeDS.rejected, (state) => {
        if (typeof state.purchase.threeDS !== 'undefined') {
          state.purchase.threeDS.status = ThreeDSecureStatus.Error
        }
      })
      .addCase(finaliseThreeDS.pending, (state) => {
        state.purchase = {
          ...state.purchase,
          threeDS: { status: ThreeDSecureStatus.Finalising },
        }
      })
      .addCase(finaliseThreeDS.fulfilled, (state) => {
        if (typeof state.purchase.threeDS !== 'undefined') {
          state.purchase.threeDS.status = ThreeDSecureStatus.Finalised
        }
      })
      .addCase(finaliseThreeDS.rejected, (state) => {
        if (typeof state.purchase.threeDS !== 'undefined') {
          state.purchase.threeDS.status = ThreeDSecureStatus.Error
        }
      })
      .addCase(closeCheckout, () => topazInitialState)
      .addCase(PURGE, () => topazInitialState)
      .addCase(retrieveCards.pending, (state) => {
        state.purchase.portalApiError = undefined
      })
      .addCase(setSessionExpired, () => topazInitialState)
      .addCase(retrieveCards.rejected, (state, action) => {
        state.purchase.status = TopazStatus.FailedToRetrieveNewCard
        const errorCode = isPortalApiError(action.payload) ? action.payload.errorCode : ''
        state.purchase.portalApiError = {
          message: 'portalApiCheckoutRetrieveCardsError',
          errorCode,
        }
      })
      .addMatcher(setupPayment.matchPending, (state) => {
        state.purchase.status = TopazStatus.Saving
      })
      .addMatcher(setupPayment.matchRejected, (state) => {
        state.purchase.status = TopazStatus.Error
      })
      .addMatcher(setupPayment.matchFulfilled, (state, action) => {
        switch (action.payload.resultCode) {
          case ResultCode.OK:
            state.purchase.status = TopazStatus.Saved
            break
          default:
            state.purchase.status = TopazStatus.Error
            break
        }
      })
      .addMatcher(setupOrder.matchPending, (state) => {
        state.purchase.status = TopazStatus.Saving
      })
      .addMatcher(setupOrder.matchFulfilled, (state, action) => {
        // used for save payment method flow, status should only be updated
        // if result code is not ok handle topaz errors
        if (action.payload.resultCode !== ResultCode.OK) {
          const topazResult = handleTopazResultStatus(action.payload)
          state.purchase.status = topazResult
        }
      })
      .addMatcher(setupOrder.matchRejected, (state) => {
        state.purchase.status = TopazStatus.Error
      })
      .addMatcher(initiateNew.matchPending, (state) => {
        state.purchase.portalApiError = undefined
        state.purchase.status = TopazStatus.Saving
      })
      .addMatcher(initiateNew.matchRejected, (state, action) => {
        state.purchase.status = TopazStatus.Error
        const errorCode = isPortalApiError(action.payload?.data) ? action.payload?.data.errorCode : ''
        state.purchase.portalApiError = {
          errorCode,
          message: 'portalApiCreditCardsInitiateNewError',
        }
      })
      .addMatcher(confirmNew.matchPending, (state) => {
        state.purchase.status = TopazStatus.Saving
      })
      .addMatcher(confirmNew.matchRejected, (state, action) => {
        const errorCode = isPortalApiError(action.payload?.data) ? action.payload?.data.errorCode : ''

        if (errorCode === ErrorCode.CashAppCardIneligible) {
          state.purchase.status = TopazStatus.CashAppCardIneligible
        } else {
          state.purchase.status = TopazStatus.Error
        }

        state.purchase.portalApiError = {
          errorCode,
          message: 'portalApiCreditCardsConfirmNewError',
        }
      })
  },
})

export const {
  selectPaymentMethod,
  resetThreeDSState,
  setLastSuccessfulSetup,
  setHasUserInitiatedChangedPaymentMethod,
  setPreferredCardError,
  selectPaymentMethodWithoutStatus,
  updateTopazApiErrorMessage,
  setCashAppPayDetails,
} = topazSlice.actions

export default topazSlice.reducer
